/**
 * PKCE OAuth 2.0 flow helpers for rule42-cloud-web.
 *
 * Implements Authorization Code + PKCE (S256) against rule42-auth OIDC.
 * Tokens are stored in sessionStorage (no localStorage — XSS-safer for SPAs).
 *
 * Flow:
 *  1. login()     → generate verifier/challenge, store state, redirect to /auth/authorize
 *  2. callback()  → exchange code for tokens, persist session, return decoded claims
 *  3. logout()    → clear session, redirect to end_session endpoint
 *  4. getToken()  → return current access_token (or null if not authed)
 *
 * @module apps/web/lib/auth
 * @since W2.9
 */

const ISSUER        = import.meta.env.VITE_AUTH_ISSUER    ?? 'https://auth.rule42.ai';
const CLIENT_ID     = import.meta.env.VITE_AUTH_CLIENT_ID ?? '59b49170-f7ee-415b-a064-c5f64d9ab3cc';
const REDIRECT_URI  = import.meta.env.VITE_AUTH_REDIRECT_URI  ?? `${window.location.origin}/auth/callback`;
const POST_LOGOUT   = import.meta.env.VITE_AUTH_POST_LOGOUT_URI ?? `${window.location.origin}/`;

// RFC 8707 resource indicator. Without it rule42-auth falls back to its
// defaultResource (`urn:rule42:cloud-api`) and mints a token with audience
// `rule42-cloud-api`, which the router rejects as `jwt audience invalid`.
const RESOURCE      = import.meta.env.VITE_AUTH_RESOURCE ?? 'urn:rule42:router-api';

const SESSION_KEY   = 'r42:session';
const PKCE_KEY      = 'r42:pkce';
const LOGOUT_FLAG   = 'r42:post-logout';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TokenClaims {
  sub: string;
  email: string;
  name?: string;
  picture?: string;
  org_id?: string;
  org_name?: string;
  org_role?: string;
  permissions?: string[];
  permissions_hash?: string;
  permissions_count?: number;
  tier?: string;
  exp: number;
  iat: number;
}

export interface AuthSession {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  claims: TokenClaims;
  expiresAt: number;       // unix seconds
}

interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
}

interface PkceState {
  verifier: string;
  state: string;
  nonce: string;
  returnTo: string;
}

let refreshPromise: Promise<AuthSession | null> | null = null;

// ── Crypto helpers ────────────────────────────────────────────────────────────

function randomBase64Url(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function sha256Base64Url(plain: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function parseJwt(token: string): TokenClaims {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT structure');
  const payload = atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(payload) as TokenClaims;
}

// ── Session storage ───────────────────────────────────────────────────────────

function readStoredSession(): AuthSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

function isExpired(session: AuthSession, skewSeconds = 0): boolean {
  return (Date.now() / 1000) >= (session.expiresAt - skewSeconds);
}

export function getSession(): AuthSession | null {
  const session = readStoredSession();
  if (!session) return null;

  // Keep refreshable sessions available so callers can silently renew them.
  if (isExpired(session) && !session.refreshToken) {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }

  return session;
}

function setSession(session: AuthSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(PKCE_KEY);
}

/**
 * Re-reads permissions from the stored session's JWT access token.
 *
 * Note: The OIDC userinfo endpoint cannot be used for permission refresh
 * because resource-bound JWT access tokens (via Resource Indicators) are
 * rejected by oidc-provider's userinfo endpoint. To get updated permissions,
 * a full re-login is required.
 *
 * Returns the current session, or null if not authenticated.
 */
export function refreshPermissions(): AuthSession | null {
  return getSession();
}

/** Returns true if the user just logged out (prevents auto-re-login). */
export function isPostLogout(): boolean {
  return sessionStorage.getItem(LOGOUT_FLAG) === '1';
}

/** Clear the post-logout flag (call when user explicitly clicks Sign In). */
export function clearPostLogout(): void {
  sessionStorage.removeItem(LOGOUT_FLAG);
}

export function getToken(): string | null {
  const session = getSession();
  if (!session || isExpired(session)) {
    return null;
  }
  return session.accessToken;
}

export function isAuthenticated(): boolean {
  return getSession() !== null;
}

async function exchangeRefreshToken(session: AuthSession): Promise<AuthSession | null> {
  if (!session.refreshToken) {
    return isExpired(session) ? null : session;
  }

  try {
    const { token_endpoint } = await getOidcConfig();
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: session.refreshToken,
      resource: RESOURCE,
    });

    const res = await fetch(token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!res.ok) {
      clearSession();
      return null;
    }

    const tokens = (await res.json()) as TokenResponse;
    const claims = parseJwt(tokens.access_token);
    const expiresAt = Math.floor(Date.now() / 1000) + tokens.expires_in;
    const nextSession: AuthSession = {
      accessToken: tokens.access_token,
      idToken: tokens.id_token ?? session.idToken,
      refreshToken: tokens.refresh_token ?? session.refreshToken,
      claims,
      expiresAt,
    };

    setSession(nextSession);
    return nextSession;
  } catch {
    clearSession();
    return null;
  }
}

export async function ensureValidSession(forceRefresh = false, minValiditySeconds = 60): Promise<AuthSession | null> {
  const session = readStoredSession();
  if (!session) {
    return null;
  }

  if (!forceRefresh && !isExpired(session, minValiditySeconds)) {
    return session;
  }

  if (!session.refreshToken) {
    if (isExpired(session)) {
      clearSession();
      return null;
    }
    return session;
  }

  if (!refreshPromise) {
    refreshPromise = exchangeRefreshToken(session).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

// ── OIDC discovery helpers ────────────────────────────────────────────────────

interface OidcConfig {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
  userinfo_endpoint?: string;
}

let cachedOidcConfig: OidcConfig | null = null;

async function getOidcConfig(): Promise<OidcConfig> {
  if (cachedOidcConfig) return cachedOidcConfig;
  const res = await fetch(`${ISSUER}/.well-known/openid-configuration`);
  if (!res.ok) throw new Error('Failed to fetch OIDC discovery document');
  cachedOidcConfig = (await res.json()) as OidcConfig;
  return cachedOidcConfig;
}

// ── Login ─────────────────────────────────────────────────────────────────────

/**
 * Initiates the PKCE authorization code flow.
 * Stores PKCE state in sessionStorage then redirects to rule42-auth.
 *
 * @param returnTo — path to redirect to after successful callback (default: '/')
 * @param opts.prompt — OIDC prompt parameter (e.g. 'login' to force credential entry)
 */
export async function login(returnTo = '/', opts?: { prompt?: string }): Promise<void> {
  const { authorization_endpoint } = await getOidcConfig();

  const verifier  = randomBase64Url(64);
  const challenge = await sha256Base64Url(verifier);
  const state     = randomBase64Url(32);
  const nonce     = randomBase64Url(32);

  const pkceState: PkceState = { verifier, state, nonce, returnTo };
  sessionStorage.setItem(PKCE_KEY, JSON.stringify(pkceState));

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             CLIENT_ID,
    redirect_uri:          REDIRECT_URI,
    scope:                 'openid profile email org permissions offline_access',
    state,
    nonce,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
    resource:              RESOURCE,
  });

  // Request consent so offline_access actually yields a refresh token.
  // When a caller already forces login, include both values.
  params.set('prompt', opts?.prompt ? `${opts.prompt} consent` : 'consent');

  // Clear post-logout flag since user is intentionally logging in
  sessionStorage.removeItem(LOGOUT_FLAG);

  window.location.href = `${authorization_endpoint}?${params.toString()}`;
}

// ── Callback ──────────────────────────────────────────────────────────────────

/**
 * Handles the OIDC callback. Exchanges the authorization code for tokens,
 * stores the session, and returns the returnTo path.
 *
 * Must be called on the /auth/callback route.
 */
export async function handleCallback(): Promise<{ returnTo: string }> {
  const params = new URLSearchParams(window.location.search);
  const code  = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) {
    throw new Error(`Authorization error: ${error} — ${params.get('error_description') ?? ''}`);
  }
  if (!code || !state) {
    throw new Error('Missing code or state in callback parameters');
  }

  const raw = sessionStorage.getItem(PKCE_KEY);
  if (!raw) throw new Error('No PKCE state found — please restart the login flow');

  const pkceState = JSON.parse(raw) as PkceState;
  if (pkceState.state !== state) throw new Error('State mismatch — possible CSRF attack');

  const { token_endpoint } = await getOidcConfig();

  const body = new URLSearchParams({
    grant_type:    'authorization_code',
    client_id:     CLIENT_ID,
    redirect_uri:  REDIRECT_URI,
    code,
    code_verifier: pkceState.verifier,
    resource:      RESOURCE,
  });

  const res = await fetch(token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Token exchange failed (${res.status}): ${detail}`);
  }

  const tokens = (await res.json()) as TokenResponse;
  if (!tokens.id_token) {
    throw new Error('Token exchange failed: missing id_token');
  }

  const claims    = parseJwt(tokens.access_token);
  const expiresAt = Math.floor(Date.now() / 1000) + tokens.expires_in;

  // Permissions are embedded in the JWT access token via the auth server's
  // extraTokenClaims hook. Userinfo cannot be used as a fallback because
  // resource-bound JWT tokens (via Resource Indicators) are rejected by
  // oidc-provider's userinfo endpoint with 401 invalid_token.

  setSession({
    accessToken:  tokens.access_token,
    idToken:      tokens.id_token,
    refreshToken: tokens.refresh_token,
    claims,
    expiresAt,
  });

  sessionStorage.removeItem(PKCE_KEY);

  return { returnTo: pkceState.returnTo };
}

// ── Logout ────────────────────────────────────────────────────────────────────

/**
 * Clears the local session and redirects to the OIDC end_session endpoint.
 */
export async function logout(): Promise<void> {
  const session = getSession();
  clearSession();

  // Set flag so the login page does NOT auto-start the OIDC flow
  sessionStorage.setItem(LOGOUT_FLAG, '1');

  try {
    const { end_session_endpoint } = await getOidcConfig();
    if (end_session_endpoint) {
      // Use the registered post_logout_redirect_uri (must match OIDC client config)
      const params = new URLSearchParams({
        post_logout_redirect_uri: POST_LOGOUT,
        ...(session?.idToken ? { id_token_hint: session.idToken } : {}),
      });
      window.location.href = `${end_session_endpoint}?${params.toString()}`;
      return;
    }
  } catch {
    // If discovery fails, just navigate to login
  }

  window.location.href = `${window.location.origin}/login`;
}
